/* @flow */

import { createSelector } from '@reduxjs/toolkit';
import {
  hydrateBraindate,
  hydrateBraindates,
} from '@braindate/domain/lib/braindate/util';

import {
  getBraindatesUnreadMessageCount,
  getBraindateInvitations,
  getConfirmedBraindates,
} from 'src/shared/domain/braindate/util/braindateUtil';

import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { State } from 'src/shared/core/type/reduxTypes';

const getAllBraindatesData = (state) => state.app.braindates.all;
const getAllBraindatesResult = (state) => state.app.braindates.all.result;
const getEntities = (state) => state.domain.entities;

export const areAllBraindatesFetching = (state: State): boolean =>
  state.app.braindates.all.isFetching;

export const getAllBraindates: (state: State) => Array<Braindate> =
  createSelector(
    [getAllBraindatesResult, getEntities],
    (allBraindates, entities) => hydrateBraindates(allBraindates, entities),
  );

/*
 |------------------------------------------------------------------------------
 | Notifications
 |------------------------------------------------------------------------------
 */

export const getAllBraindatesNotificationCount: (state: Object) => number =
  createSelector(
    [getAllBraindatesResult, getEntities],
    (allBraindates, entities) => {
      const braindates = hydrateBraindates(allBraindates, entities);
      const notificationCount = getBraindatesUnreadMessageCount(braindates);

      return notificationCount;
    },
  );

export const getBraindateInvitationsNotificationCount: (
  state: Object,
) => number = createSelector(
  [getAllBraindatesResult, getEntities],
  (allBraindates, entities) => {
    const braindates = hydrateBraindates(allBraindates, entities);
    const invitations = getBraindateInvitations(braindates);
    const notificationCount = getBraindatesUnreadMessageCount(invitations);

    return notificationCount;
  },
);

export const getConfirmedBraindatesNotificationCount: (
  state: Object,
) => number = createSelector(
  [getAllBraindatesResult, getEntities],
  (allBraindates, entities) => {
    const braindates = hydrateBraindates(allBraindates, entities);
    const confirmedBraindates = getConfirmedBraindates(braindates);
    const notificationCount =
      getBraindatesUnreadMessageCount(confirmedBraindates);

    return notificationCount;
  },
);

/*
 |------------------------------------------------------------------------------
 | Single Braindate
 |------------------------------------------------------------------------------
 */

const _getBraindateId = (state, props) => {
  const { id } = props;

  return id;
};

export const getBraindate: (state: Object, props: Object) => Braindate | void =
  createSelector(
    [_getBraindateId, getEntities],
    (result, entities): Braindate | void => {
      if (result) {
        return hydrateBraindate(result, entities);
      }
    },
  );

export const getBraindateFetching: (state: Object, props: Object) => boolean =
  createSelector([_getBraindateId, getAllBraindatesData], (id, data): boolean =>
    data[id] ? data[id].isFetching : false,
  );
